import {
	AccessCodeChange,
	AccessCodeShape,
	Box,
	Device,
	DeviceType,
	EndOccupancyOptions,
	Lock,
	LockStatus,
	LogLevel,
	Occupancy,
	OccupancyContent,
} from "@variocube/app-api";
import {defined, generate, generateCode, getRandomElement, sleep} from "@variocube/app-common";
import {DateTime} from "luxon";
import * as packageJson from "../../../../package.json";
import {logger} from "../logger";
import {AbstractController} from "./AbstractController";
import {AccessLevel, Controller, ControllerOccupyRequest, Permissions, SupportInfo} from "./Controller";
import {MockBarcodeReader} from "./MockBarcodeReader";
import {MockDoorbell} from "./MockDoorbell";
import {MockKeypad} from "./MockKeypad";
import {MockNfcReader} from "./MockNfcReader";
import {MockPaymentTerminal} from "./MockPaymentTerminal";

const log = logger.sub("MockController");

type StringKeyed = { [key: string]: any };

const ResponseDelay = 100;

const mockPermissions: { [key: string]: Permissions } = {
	"1": {accessLevel: AccessLevel.ReadOnly},
	"2": {accessLevel: AccessLevel.OpenNonOccupied},
	"3": {accessLevel: AccessLevel.FullAccess},
};

async function responseDelay() {
	await sleep(ResponseDelay);
}

const DEVICES: Device[] = [
	{id: "mock-locking", vendor: "Variocube", model: "Mock Locking", types: [DeviceType.Locking]},
	{id: "mock-barcode", vendor: "Variocube", model: "Mock Barcode Reader", types: [DeviceType.BarcodeReader]},
	{id: "mock-nfc", vendor: "Variocube", model: "Mock NFC Reader", types: [DeviceType.NfcReader]},
	{
		id: "mock-payment-terminal",
		vendor: "Variocube",
		model: "Mock Payment Terminal",
		types: [DeviceType.PaymentTerminal],
	},
];

export class MockController extends AbstractController implements Controller {
	private readonly data: StringKeyed = {};

	private readonly locks: Lock[];
	private readonly boxes: Box[];
	private readonly occupancies = new Map<string, Occupancy>();
	private readonly pendingOccupancies = new Map<string, Occupancy>();
	private readonly endedOccupancies = new Map<string, Occupancy>();

	public readonly nfcReader = new MockNfcReader();
	public readonly paymentTerminal = new MockPaymentTerminal();
	public readonly barcodeReader = new MockBarcodeReader();
	public readonly keypad = new MockKeypad();
	public readonly doorbell = new MockDoorbell();

	private nfc = true;
	private barcode = true;

	private static apps: string[] = [
		"variocube.pobox",
		"cube.clesyclean",
		"cube.service",
		"cube.lottery",
		"cube.safe",
		"dx7-synth",
		"youtube",
		"midisynth",
		"radio-oe1",
		"variocube.order",
		"variocube.inhouse",
		"variocube.shutterspeed",
		"variocube.rental",
		"variocube.vending",
		"variocube.carrier",
	];

	failOnOpen: boolean = false;
	ignoreOpen: boolean = false;

	constructor() {
		super();

		this.locks = generate(24, (ordinal) => ({
			handle: `lock${ordinal}`,
			status: LockStatus.Closed,
		}));

		this.boxes = generate(24, (ordinal) => ({
			number: `${ordinal}`,
			description: `Box ${ordinal}`,
			lock: `lock${ordinal}`,
			secondaryLock: "lock12",
			types: [
				getRandomElement(["S", "M", "L" /*, "XL", "XXL"*/]),
				getRandomElement(["SuitBag", "WelcomePackage"]),
				getRandomElement(["Storage", "Hängefach", "Room"]),
				getRandomElement(["S 32", "M 32", "LG46", "LG20"]),
				getRandomElement(["S-Charger", "Ski", "PrivateBox"]),
				"Any",
			],
			// disable every 6th box
			disabled: ordinal % 6 == 0,
			// equally distribute box features, but don't assign multiple features
			accessible: ordinal % 4 == 0,
			cooled: ordinal % 4 == 1,
			dangerousGoods: ordinal % 4 == 2,
			charger: ordinal % 4 == 3,
		}));

		window.setTimeout(() => this.onConnected(true), 1000);

		window.setInterval(this.generateLocationCode, 60 * 1000);
		this.generateLocationCode();
	}

	async getInternetConnection() {
		await responseDelay();
		return {
			connected: navigator.onLine,
		};
	}

	private generateLocationCode = () => {
		this.setLocationCode(generateCode(4));
	};

	get version() {
		return packageJson.version;
	}

	get hostname() {
		return location.hostname;
	}

	get stage() {
		return "mock";
	}

	get region() {
		return "mock";
	}

	get port() {
		return Number.parseInt(location.port);
	}

	async getDevices() {
		return this.getEnabledDevices();
	}

	private getEnabledDevices() {
		return DEVICES
			.filter(device => !device.types.includes(DeviceType.BarcodeReader) || this.barcode)
			.filter(device => !device.types.includes(DeviceType.NfcReader) || this.nfc);
	}

	isBarcodeEnabled() {
		return this.barcode;
	}

	setBarcodeEnabled(enabled: boolean) {
		this.barcode = enabled;
		this.onDevicesChanged(this.getEnabledDevices());
	}

	isNfcEnabled() {
		return this.nfc;
	}

	setNfcEnabled(enabled: boolean) {
		this.nfc = enabled;
		this.onDevicesChanged(this.getEnabledDevices());
	}

	async getStorageItem(appId: string, key: string) {
		await responseDelay();
		return this.data[appId + key];
	}

	async getStorageItemBlob(appId: string, key: string) {
		await responseDelay();
		const value = this.data[appId + key];
		return value && (value instanceof Blob ? value : undefined);
	}

	async writeStorageItem(appId: string, key: string, value: any) {
		await responseDelay();
		this.data[appId + key] = value;
		console.info("write storage item " + appId + ", " + key, {value: value});

		this.onStorageItemChanged(appId, key);
	}

	private getLockOrThrow(lockHandle: string) {
		const lock = this.getLock(lockHandle);
		if (!lock) {
			throw "Lock not found.";
		}
		return lock;
	}

	async open(lockHandle: string) {
		await responseDelay();
		const lock = this.getLockOrThrow(lockHandle);

		if (this.failOnOpen) {
			log.info("Letting open command fail.");
			throw new Error("Open lock failed");
		}

		if (this.ignoreOpen) {
			log.info("Letting open command time out. No lock status change will happen.");
			return;
		}

		lock.status = LockStatus.Open;
		log.info("Lock opened", lockHandle);
		this.onLockStatusChanged(lockHandle, LockStatus.Open);
	}

	async closeLock(lockHandle: string) {
		const lock = this.getLockOrThrow(lockHandle);
		lock.status = LockStatus.Closed;
		log.info("Lock closed", lockHandle);
		this.onLockStatusChanged(lockHandle, LockStatus.Closed);
	}

	async getStatus(lock: string) {
		await responseDelay();
		return this.internalGetStatus(lock);
	}

	private internalGetStatus(lock: string) {
		return this.getLockOrThrow(lock).status;
	}

	async endOccupancy(occupancyId: string, options?: EndOccupancyOptions) {
		await responseDelay();

		const {gracePeriod, content, merge} = options || {};

		const occupancy = this.occupancies.get(occupancyId);
		if (!occupancy) {
			throw new Error("Occupancy not found.");
		}

		if (content) {
			occupancy.content = {
				...(merge && occupancy.content),
				...content,
			};
		}

		this.endedOccupancies.set(occupancyId, occupancy);
		this.occupancies.delete(occupancyId);

		this.onOccupancyEnded(occupancyId);

		const deleteOccupancy = () => {
			this.endedOccupancies.delete(occupancyId);
		};

		if (defined(gracePeriod) && gracePeriod > 0) {
			setTimeout(deleteOccupancy, 1000 * gracePeriod);
		} else {
			deleteOccupancy();
		}
	}

	private getLock(lockHandle: string) {
		return this.locks.filter(lock => lock.handle == lockHandle).pop();
	}

	async occupy(request: ControllerOccupyRequest) {
		await responseDelay();

		log.info("occupy", request);

		const {content, accessCode, accessCodeShape, appId, type, boxNumber} = request;

		// helper type to always trigger the case when a box type is not available
		if (type == "Unavailable") {
			throw new Error("Reservation could not be made.");
		}

		const candidates = boxNumber
			? (box: Box) => box.number == boxNumber
			: (box: Box) => type && box.types.indexOf(type) >= 0;

		log.debug("occupy: boxes", this.boxes);

		const box = this.boxes.filter(candidates)
			.filter(box => !this.hasOccupancy(box.number))
			.pop();

		if (!box) {
			throw new Error("No box available for the specified request.");
		}

		const uuid = Math.floor((1 + Math.random()) * 0x100000).toString(16);

		const occupancy: Occupancy = {
			uuid: uuid,
			boxNumber: box.number,
			appId,
			created: new Date().toISOString(),
			accessCode: getAccessCode(accessCode, accessCodeShape),
			accessKeys: [],
			content,
		};

		this.pendingOccupancies.set(uuid, occupancy);

		log.info("Occupancy created", occupancy);

		return occupancy;
	}

	async confirmOccupancy(occupancyId: string, content?: OccupancyContent, merge?: boolean) {
		const occupancy = this.pendingOccupancies.get(occupancyId);

		if (occupancy) {
			this.occupancies.set(occupancyId, occupancy);
			this.pendingOccupancies.delete(occupancyId);
		} else {
			throw new Error("Occupancy not found.");
		}

		if (content) {
			occupancy.content = {
				...(merge && occupancy.content),
				...content,
			};
		}

		console.info("MockController: Occupancy confirmed", occupancyId);
		this.onOccupancyCreated(occupancy);
	}

	async cancelOccupancy(occupancyId: string) {
		this.pendingOccupancies.delete(occupancyId);
		console.info("MockController: Occupancy cancelled", occupancyId);
	}

	private hasOccupancy(boxNumber: string) {
		return [...this.occupancies.values()]
			.filter(occupancy => occupancy.boxNumber == boxNumber)
			.pop();
	}

	async updateOccupancy(occupancyId: string, content: OccupancyContent, merge: boolean) {
		await responseDelay();

		const occupancy = this.occupancies.get(occupancyId) || this.pendingOccupancies.get(occupancyId);
		if (!occupancy) {
			throw new Error("Occupancy not found.");
		}

		occupancy.content = {
			...(merge && occupancy.content),
			...content,
		};
		log.info("Occupancy updated", occupancyId, content);

		this.onOccupancyUpdated(occupancy);
	}

	async changeAccessCode(occupancyId: string, accessCode: string | AccessCodeChange) {
		await responseDelay();

		const occupancy = this.occupancies.get(occupancyId) || this.pendingOccupancies.get(occupancyId);
		if (!occupancy) {
			throw new Error("Occupancy not found.");
		}

		occupancy.accessCode = typeof accessCode != "string"
			? getAccessCode(accessCode.accessCode, accessCode.accessCodeShape)
			: accessCode;

		log.info("Changed access code", occupancyId, accessCode);

		this.onOccupancyAccessChanged(occupancy);
	}

	async changeAccessKeys(occupancyId: string, accessKeys: string[]) {
		await responseDelay();

		const occupancy = this.occupancies.get(occupancyId) || this.pendingOccupancies.get(occupancyId);
		if (!occupancy) {
			throw new Error("Occupancy not found.");
		}

		occupancy.accessKeys = [...accessKeys];

		log.info("Changed access keys", occupancyId, accessKeys);

		this.onOccupancyAccessChanged(occupancy);
	}

	async getOccupancies(appId?: string, access?: string) {
		await responseDelay();
		log.verbose("getOccupancies", {
			appId,
			access,
			occupancies: this.occupancies,
		});
		return [...this.occupancies.values()].filter(occupancy => {
			if (appId) {
				if (occupancy.appId != appId) {
					return false;
				}
			}
			if (access) {
				if (occupancy.accessCode != access && occupancy.accessKeys.indexOf(access) < 0) {
					return false;
				}
			}
			return true;
		});
	}

	async getLocks() {
		await responseDelay();
		return this.locks;
	}

	async getBoxes() {
		await responseDelay();
		return this.boxes;
	}

	async updateBoxMaintenanceState(boxNumber: string, requiredMaintenance: boolean) {
		await responseDelay();
		for (let box of this.boxes) {
			if (box.number === boxNumber) {
				box.maintenanceRequiredAt = requiredMaintenance ? DateTime.now().toJSDate().toISOString() : undefined;
			}
		}
	}

	async getOccupancy(occupancyId: string) {
		await responseDelay();
		return this.occupancies.get(occupancyId)
			|| this.pendingOccupancies.get(occupancyId)
			|| this.endedOccupancies.get(occupancyId);
	}

	async getApps() {
		await responseDelay();
		return [...MockController.apps];
	}

	async verifyAuthCode(code: string): Promise<Permissions> {
		await responseDelay();
		const permissions = mockPermissions[code];
		if (permissions) {
			return permissions;
		} else {
			throw new Error("invalid auth code");
		}
	}

	async getSupportInfo(): Promise<SupportInfo> {
		await responseDelay();
		return {
			email: "support@variocube.com",
			freetext: "",
			operatedBy: "Variocube",
			phone: "+54 720 676 300",
			website: "https://variocube.com",
		};
	}

	/**
	 * Restarts the controller.
	 */
	async restartController() {
		await responseDelay();
	}

	/**
	 * Restarts the operating system.
	 */
	async restartOperatingSystem() {
		await responseDelay();
	}

	/**
	 * Restarts the user interface.
	 */
	async restartUserInterface() {
		await responseDelay();
	}

	/**
	 * Updates the software to currently defined version.
	 */
	async updateSoftware() {
		await responseDelay();
	}

	/**
	 * Shuts the cube down.
	 */
	async shutdown() {
		await responseDelay();
	}

	/**
	 * Logs the specified message.
	 * @return Promise that resolves if the log entry was successfully created.
	 */
	async log(level: LogLevel, source: string, message: string, data: any) {
		const logRequest = {level, source, message, data};
		console.info("LOG", logRequest);
	}
}

function getAccessCode(accessCode?: string, accessCodeShape?: AccessCodeShape) {
	if (accessCode) {
		return accessCode;
	}
	if (!accessCodeShape) {
		throw new Error("Neither accessCode nor accessCodeShape is specified.");
	}
	return generateAccessCode(accessCodeShape);
}

function generateAccessCode(shape: AccessCodeShape) {
	const chars = [...shape.alphabet];
	const accessCode = [];
	for (let i = 0; i < shape.length; i++) {
		accessCode.push(getRandomElement(chars));
	}
	return accessCode.join("");
}
